import { AsyncPipe } from '@angular/common';
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    HostBinding,
    Input,
    OnChanges,
    Output,
    SimpleChanges,
    TemplateRef,
    ViewChild,
    inject,
    input,
} from '@angular/core';

import { BetBuilderProvider } from '@cds/betting-offer/add-ons';
import { OptionGroupType, ScoreboardFactory } from '@frontend/sports/betting-offer/feature/fixture-factories';
import { EventMediaHelperService } from '@frontend/sports/betting-offer/feature/media';
import { LeaderboardGameScore } from '@frontend/sports/betting-offer/feature/model';
import {
    EpcotConfig,
    FavouritesConfig,
    GridConfig,
    LiveSectionConfig,
    Sitecore,
    SitecoreEventDetailsConfig,
    SitecoreGameMessagesConfig,
} from '@frontend/sports/common/client-config-data-access';
import { SportConstant } from '@frontend/sports/common/core/data-access/constants';
import { GridEvent } from '@frontend/sports/grid/core/feature/model';
import { ModalRef } from '@frontend/sports/modal/feature';
import { RedirectHelperService } from '@frontend/sports/navigation/core/feature/redirect';
import { MarketUrlParam, UrlHelperService } from '@frontend/sports/navigation/core/feature/url-helper';
import { PriceboostCountSignpostComponent, SignpostTypeService } from '@frontend/sports/price-boost/feature';
import { OnboardingTooltipComponent } from '@frontend/sports/tooltip-feature';
import { NOT_APPLICABLE, TrackingService, trackingConstants } from '@frontend/sports/tracking/feature';
import { NativeAppService } from '@frontend/vanilla/core';
import { IconCustomComponent } from '@frontend/vanilla/shared/icons';
import { NgxPopperjsContentComponent, NgxPopperjsModule, NgxPopperjsPlacements } from 'ngx-popperjs';
import { ReplaySubject, filter, map, switchMap } from 'rxjs';

import { BetBuilderTrackingValues } from '../../bet-builder-core/bet-builder-tracking';
import { BetBuilderOverlayHeaderComponent } from '../../bet-builder-core/components/bet-builder-overlay-header/bet-builder-overlay-header.component';
import { BetBuilderConfigService } from '../../bet-builder-core/services/bet-builder-config.service';
import { BetBuilderOverlayService } from '../../bet-builder-core/services/bet-builder-overlay.service';
import { getBetbuilderTypeValue, isBetbuilderEvent } from '../../bet-builder-core/utils';
import { AlertButtonComponent } from '../../native-alerts/alert-button/alert-button.component';
import { OptionGroupTeamStatisticsService } from '../../option-group/player-statistics/option-group-team-statistics.service';
import { EventTimerComponent } from '../../timers/event-timer.component';
import { EventInfoToolTipService } from './event-info-tooltip.service';
import { RaceInfoComponent } from './race-info.component';

@Component({
    selector: 'ms-event-info',
    templateUrl: './event-info.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    styleUrls: ['./event-info.component.scss'],
    imports: [
        AlertButtonComponent,
        AsyncPipe,
        BetBuilderOverlayHeaderComponent,
        EventTimerComponent,
        IconCustomComponent,
        NgxPopperjsModule,
        OnboardingTooltipComponent,
        PriceboostCountSignpostComponent,
        RaceInfoComponent,
    ],
    providers: [OptionGroupTeamStatisticsService],
})
export class EventInfoComponent implements OnChanges {
    sitecoreGameMessage = inject(SitecoreGameMessagesConfig);
    sitecore = inject(Sitecore);
    sitecoreEventDetailsConfig = inject(SitecoreEventDetailsConfig);
    private urlHelper = inject(UrlHelperService);
    private redirectHelperService = inject(RedirectHelperService);
    private betBuilderOverlayService = inject(BetBuilderOverlayService);
    private eventInfoToolTipService = inject(EventInfoToolTipService);
    private betBuilderConfigService = inject(BetBuilderConfigService);
    private tracker = inject(TrackingService);
    private nativeAppservice = inject(NativeAppService);
    private epcotConfig = inject(EpcotConfig);
    private scoreboardFactory = inject(ScoreboardFactory);
    private liveSectionConfig = inject(LiveSectionConfig);
    private cdr = inject(ChangeDetectorRef);
    private favouritesConfig = inject(FavouritesConfig);
    private signpostTypeService = inject(SignpostTypeService, { optional: true });
    private fallbackStatsService = inject(OptionGroupTeamStatisticsService);
    private inheritedStatsService = inject(OptionGroupTeamStatisticsService, { optional: true, skipSelf: true });

    @Input() event: GridEvent;
    @Input() isOutrightMarket: boolean;
    @Input() mediaEnabled: boolean;
    @Input() disableBadges = false;
    @Input() hideFavouriteIcon = false;
    @Input() showEventTimerInfoSection?: boolean = true;
    @Input() showFixedFooterSection?: boolean = true;
    @Output() betbuilderIconClicked = new EventEmitter();

    showPlayerStatisticsButton = input(false);

    @HostBinding('class') className = 'grid-event-info';
    modal: ModalRef;
    showIcon: boolean;
    liveIconText: string;
    isLeagueChildPageDesign: boolean;
    liveEventInfo?: string;
    showVideoIcon: boolean;
    tooltipText: string;
    private event$ = new ReplaySubject<GridEvent>();
    broadcaster$ = this.event$.pipe(
        filter((event) => !event?.scoreboard?.started),
        switchMap((event) => this.sitecoreGameMessage.whenReady.pipe(map(() => this.sitecoreGameMessage.secondaryBroadcastInfo?.messages[event.id]))),
    );

    @ViewChild('header') headerTemplate: TemplateRef<any>;

    @ViewChild('tooltipPopper') tooltipPopper: NgxPopperjsContentComponent;

    private enableSGPNavigationOverride: boolean;
    private readonly mediaHelperService = inject(EventMediaHelperService);

    priceBoostCount?: number;

    constructor() {
        const gridConfig = inject(GridConfig);

        this.showIcon = this.nativeAppservice.isTerminal;
        this.liveIconText =
            (this.epcotConfig.isEnabled && gridConfig.is2way3wayEnabled) || gridConfig.gridEventDisplayVersion === 'V2'
                ? ''
                : this.sitecore.event.Live;
        this.isLeagueChildPageDesign = gridConfig.isLeagueChildPageDesign;
        this.enableSGPNavigationOverride = gridConfig.enableSGPNavigationOverride;
    }

    get optionGroupTeamStatisticsService(): OptionGroupTeamStatisticsService {
        return this.inheritedStatsService ?? this.fallbackStatsService;
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.event?.previousValue !== changes.event?.currentValue) {
            this.optionGroupTeamStatisticsService.initializePlayerStatistics(this.event, OptionGroupType.SixPack);
        }
        if (changes.event) {
            this.showVideoIcon = !this.mediaHelperService.hasPerformV2FastData(this.event);

            this.liveEventInfo = this.getLiveEventInfo(this.event.sport.id, this.event.league.parentLeagueId);
            this.priceBoostCount = this.getPriceBoost();
            this.event$.next(this.event);

            this.sitecoreEventDetailsConfig.whenReady.subscribe(() => {
                const leagueId = this.event.league?.id;
                const onboarding = this.sitecoreEventDetailsConfig.onboarding;

                this.tooltipText = leagueId
                    ? onboarding[`LiveSgpAvailableTooltipLeague_${leagueId}`] || onboarding[`LiveSgpAvailableTooltip`] || ''
                    : onboarding[`LiveSgpAvailableTooltip`] || '';

                this.cdr.markForCheck();
            });
        }
    }

    ngOnDestroy(): void {
        const isAngstromEvent = this.event.betBuilderInfo?.betBuilderProvider === BetBuilderProvider.Angstrom;

        if (isAngstromEvent && this.event.live) {
            this.onCloseAngstromLiveSgpTooltip();
        }
    }

    get raceInfo(): boolean {
        const score = this.event.scoreboard.getScore<LeaderboardGameScore>()!;
        const raceSport = this.event.sport.id === SportConstant.Formula1 || this.event.sport.id === SportConstant.Motorbikes;

        return raceSport && this.event.scoreboard.started && score && score.type && !score.preRace;
    }

    get showBetBuilderIcon(): boolean {
        return !this.disableBadges && isBetbuilderEvent(this.event, this.betBuilderConfigService) && !!this.showFixedFooterSection;
    }

    get shouldShowLiveSgpTooltip(): boolean {
        return (
            this.eventInfoToolTipService.shouldShowLiveSgpTooltip(this.event.betBuilderInfo?.betBuilderProvider, this.event.league.id) &&
            this.event.live
        );
    }

    get showFavouriteIcon(): boolean {
        return this.favouritesConfig.isFavouritesEnabled && !!this.event.favourited && !this.hideFavouriteIcon;
    }

    builderClick($event: MouseEvent) {
        $event.stopPropagation();
        $event.preventDefault();

        this.tracker.track(trackingConstants.EVENT_TRACKING, {
            [trackingConstants.COMPONENT_CATEGORY_EVENT]: BetBuilderTrackingValues.BetBuilder,
            [trackingConstants.COMPONENT_LABEL_EVENT]: BetBuilderTrackingValues.BuildaBetPod,
            [trackingConstants.COMPONENT_ACTION_EVENT]: BetBuilderTrackingValues.Click,
            [trackingConstants.COMPONENT_POSITION_EVENT]: BetBuilderTrackingValues.ProductIcon,
            [trackingConstants.COMPONENT_LOCATION_EVENT]: this.event.name,
            [trackingConstants.COMPONENT_EVENT_DETAILS]: BetBuilderTrackingValues.BuildaBet,
            [trackingConstants.COMPONENT_URL_CLICKED]: NOT_APPLICABLE,
            [trackingConstants.SPORT_ID]: this.event.sport.id.toString(),
            [trackingConstants.SPORT_LEAGUE_ID]: this.event.league.id.toString(),
        });

        if (this.betBuilderConfigService.showEntainUiInOverlay(this.event.sport.id)) {
            this.openEntainBetBuilderOverlay();
        } else {
            this.openEventPageBetBuilderMarket();
        }

        this.betbuilderIconClicked.emit();
    }

    openEntainBetBuilderOverlay() {
        this.betBuilderOverlayService.openBuilder({
            headerTemplate: this.headerTemplate,
            event: this.event,
            source: BetBuilderTrackingValues.EventInfo,
            isOverlay: true,
        });
    }

    openEventPageBetBuilderMarket() {
        const eventUrl = this.enableSGPNavigationOverride
            ? this.urlHelper.getEventUrl(this.event)
            : this.urlHelper.getEventUrl(this.event, getBetbuilderTypeValue(this.event, MarketUrlParam));
        this.redirectHelperService.goToPage(eventUrl);
    }

    private getLiveEventInfo(sportsId: number, leagueId?: number): string | undefined {
        const scoreboard = this.event.scoreboard;
        if (!scoreboard.started || !leagueId || !this.liveSectionConfig.liveEventsInfoForLeagueIds.includes(leagueId)) {
            return;
        }

        return this.scoreboardFactory.getSportsLiveEventInfo(sportsId, scoreboard);
    }

    getPriceBoost(): number | undefined {
        if (this.signpostTypeService?.isPriceBoost && this.showFixedFooterSection && this.event.priceBoostCount && this.event.priceBoostCount > 0) {
            return this.event.priceBoostCount;
        }

        return undefined;
    }

    onCloseAngstromLiveSgpTooltip(): void {
        this.eventInfoToolTipService.closeAngstromLiveSgpTooltip(this.event.league.id);
        this.cdr.markForCheck();
    }

    toggleTeamStatistics(event: MouseEvent): void {
        event.stopPropagation();
        event.preventDefault();
        this.optionGroupTeamStatisticsService.toggleOpen(this.event);
    }

    protected readonly NgxPopperjsPlacements = NgxPopperjsPlacements;
}
